import useMobileDetect from "@carotek/hooks/UsMobileDetect";
import CarotekColors from "@carotek/utils/Colors";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Button from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import User from "@insite/mobius/Icons/User";
import Menu, { MappedLink } from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
}

const mapStateToProps = (state: ApplicationState) => ({
    isSignInPage: getCurrentPage(state).type === "SignInPage",
    userName: state.context.session.userLabel || state.context.session.userName,
    myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
    signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
    currentUserIsGuest: getCurrentUserIsGuest(state),
    isPunchOutSession: getIsPunchOutSession(state),
    currentLocation: getLocation(state),
    displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
    displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
    showInventory: state.context.showInventory,
    showPricing: state.context.showPricing,
    isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
    hasChangeLastQty:
        state.pages.vmiBins.selectVmiBinsChangedQty &&
        Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
    orderConfirmationPageUrl: getPageLinkByPageType(state, "OrderConfirmationPage")?.url,
    onePageCheckoutConfirmationPageUrl: getPageLinkByPageType(state, "OnePageCheckoutConfirmationPage")?.url,
    changePasswordPage: getPageLinkByPageType(state, "ChangePasswordPage"),
    changeCustomerPage: getPageLinkByPageType(state, "ChangeCustomerPage"),
    session: state.context.session,
    billto: getBillToState(state, state.context.session.billToId).value,
    shipto: getShipToState(state, state.context.session.shipToId).value,
});

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    profileClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    userTitle?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    container?: InjectableCss;
    usernameContainer?: InjectableCss;
    drawerStyles?: DrawerPresentationProps;
    infoTitle?: TypographyPresentationProps;
    infoContainer?: TypographyPresentationProps;
    userNameTitle?: InjectableCss;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 6px 20px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            padding: 0;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}

            :hover {
                span,
                svg {
                    color: ${getColor("danger")};
                }
            }

            [class^="MenuStyle"] {
                text-align: left;
                z-index: 9999;

                [class^="MenuItemStyle"] {
                    a {
                        min-height: auto;
                        :hover {
                            background-color: ${CarotekColors.white};
                        }
                    }
                }
                a [class^="TypographyStyle"],
                button[class^="StyledButton"] {
                    font-size: 14px;
                    color: ${getColor("text.link")};
                    padding: 7px 10px;
                    margin-left: 0;
                    text-transform: capitalize;
                }
            }
        `,
    },
    profileClickable: {
        css: css`
            margin-left: 10px;
            transform: translateY(3px);
        `,
    },
    titleClickable: {
        css: css`
            margin-left: 10px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            transform: translateY(9px);
                            margin-right: 14px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    titleIcon: {
        size: 24,
        color: `${CarotekColors.blue1}`,
        css: css``,
    },
    titleTypography: {
        css: css`
            margin-left: 10px;
            //max-width: 150px;
            letter-spacing: 0.25pt;
            display: flex;
            align-items: center;
            gap: 9px;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            font-family: "OpenSans";
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
        fontFamily: "OpenSansBold",
        color: `${CarotekColors.blue1}`,
        size: 12,
        weight: "bold",
        ellipsis: true,
        transform: "uppercase",
    },
    userTitle: {
        css: css`
            margin-left: 10px;
            //max-width: 150px;
            letter-spacing: 0.25pt;
            display: inline;
            align-items: center;
            gap: 9px;
            max-width: 199px;
            text-overflow: ellipsis;
            white-space: nowrap;
        `,
        fontFamily: "OpenSansBold",
        color: `${CarotekColors.blue1}`,
        size: 12,
        weight: "bold",
        ellipsis: true,
        transform: "uppercase",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            text-transform: uppercase;
        `,
    },
    container: {
        css: css`
            display: flex;
            // transform: translateY(4px);
        `,
    },
    usernameContainer: {
        css: css`
            display: flex;
            align-items: center;
            padding: 0 0px 0 20px;
            transform: translateY(4px);

            span[data-test-selector="header_userName"] {
                overflow: hidden;
                margin-left: 10px;
                letter-spacing: 0.25pt;
                display: inline;
                -webkit-box-align: center;
                align-items: center;
                gap: 9px;
                max-width: 199px;
                text-overflow: ellipsis;
                white-space: nowrap;
                font-family: "OpenSansBold";
                font-size: 12px;
                text-transform: uppercase;
                color: ${getColor("primary")};
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            border-right: 1px solid #ddd;
                            margin-right: 5px;
                            padding-right: 14px;
                        `,
                        null,
                        null,
                        null,
                    ],
                    "max",
                )}
        `,
    },
    drawerStyles: {
        cssOverrides: {
            drawerTitle: css`
                display: none;
            `,
            drawerContent: css`
                padding: 20px;

                button {
                    width: 100%;
                    margin: 10px 0;
                }

                button[data-test-selector="headerSignOutLink"] {
                    color: ${getColor("primary")};
                    font-size: 14px;
                }
            `,
        },
    },
    infoTitle: {
        transform: "uppercase",
        weight: 600,
        fontFamily: "OpenSansSemiBold",
        color: "text.main",
        size: 16,
        css: css`
            margin-right: 10px;
        `,
    },
    infoContainer: {
        color: "text.main",
        css: css`
            margin-bottom: 15px;
            display: block;
        `,
    },
    userNameTitle: {
        css: css`
            margin-bottom: 15px;
            margin-top: 8px;
            [class^="IconWrapper"] {
                svg {
                    transform: translateY(3px);
                    stroke: ${getColor("text.main")};
                }
            }
            span[data-test-selector="header_userName"] {
                font-size: 16px;
                font-family: "OpenSansSemiBold";
                text-transform: capitalize;
                color: ${getColor("text.main")};
            }
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    orderConfirmationPageUrl,
    onePageCheckoutConfirmationPageUrl,
    changePasswordPage,
    billto,
    shipto,
    session,
    changeCustomerPage,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);
    const [showMobileInfo, setMobileInfo] = useState(false);
    const { isMobile } = useMobileDetect();
    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    if (fields.icon === "User") {
        icon = User;
    }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    ) ?? []) as MappedLink[];

    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: uppercase;
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }

            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
                return;
            }

            const returnUrl = currentLocation.pathname + currentLocation.search;
            if (
                returnUrl === "/" ||
                (currentUserIsGuest && orderConfirmationPageUrl && returnUrl.indexOf(orderConfirmationPageUrl) > -1) ||
                (currentUserIsGuest &&
                    onePageCheckoutConfirmationPageUrl &&
                    returnUrl.indexOf(onePageCheckoutConfirmationPageUrl) > -1)
            ) {
                history.push(`${signInUrl}`);
                return;
            }

            history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
        }
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
    };

    let signInStatusText = translate("Sign In / Create Profile");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    const showInfo = () => {
        // shows info on mobile view
        if (isMobile && userName && !currentUserIsGuest) {
            setMobileInfo(true);
        }
    };

    const closeDrawer = () => {
        setMobileInfo(false);
    };

    const redirectCustomerPage = () => {
        if (changeCustomerPage?.url) {
            closeDrawer();
            history.push(changeCustomerPage?.url);
        }
    };

    return (
        <>
            <Drawer isOpen={showMobileInfo} handleClose={closeDrawer} position="top" {...styles.drawerStyles}>
                <StyledWrapper>
                    <Typography {...styles.infoContainer}>
                        <Typography as="span" {...styles.infoTitle}>
                            {translate("Customer #")}:
                        </Typography>
                        {billto?.customerNumber}
                    </Typography>
                    <Typography {...styles.infoContainer}>
                        <Typography as="span" {...styles.infoTitle}>
                            {translate("Ship To")}:
                        </Typography>
                        <Typography as="p">
                            {shipto?.companyName} {shipto?.firstName} {shipto?.lastName} {shipto?.fullAddress}
                        </Typography>
                    </Typography>
                    {session.displayChangeCustomerLink ? (
                        <Button variant="primary" onClick={redirectCustomerPage}>
                            {translate("Change Customer/Ship To")}
                        </Button>
                    ) : null}
                    {userName && (
                        <StyledWrapper {...styles.userNameTitle}>
                            {showIcon && <Icon {...styles.titleIcon} src={icon} title={userName} />}
                            {showLabel && (
                                <Typography {...styles.userTitle} data-test-selector="header_userName">
                                    {userName}
                                </Typography>
                            )}
                        </StyledWrapper>
                    )}
                    {userName && !currentUserIsGuest && (
                        <Clickable onClick={onSignOutHandler} color="primary" data-test-selector="headerSignOutLink">
                            {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                        </Clickable>
                    )}
                </StyledWrapper>
            </Drawer>
            <StyledWrapper {...styles.container}>
                {userName && (
                    <StyledWrapper {...styles.usernameContainer} onClick={showInfo}>
                        {showIcon && <Icon {...styles.titleIcon} src={icon} title={userName} />}
                        {!isMobile && showLabel && (
                            <Typography data-test-selector="header_userName">{userName}</Typography>
                        )}
                    </StyledWrapper>
                )}

                <StyledWrapper {...styles.signOutWrapper}>
                    {!isMobile && includeAccountMenu && userName && !currentUserIsGuest && (
                        <Menu
                            descriptionId="accountMenu"
                            menuItems={menuItems}
                            displayChangeCustomerLink={displayChangeCustomerLink}
                            maxDepth={1}
                            menuTrigger={
                                <Clickable {...styles.profileClickable} data-test-selector="header_signIn">
                                    {/* {showIcon && <Icon {...styles.titleIcon} src={icon} title={userName} />} */}
                                    {showLabel && (
                                        <Typography {...styles.titleTypography} data-test-selector="header_userName">
                                            {translate("My Profile")}{" "}
                                            <Icon {...styles.titleIcon} src={ChevronDown} size={20} title={userName} />
                                        </Typography>
                                    )}
                                </Clickable>
                            }
                            cssOverrides={{
                                wrapper: css`
                                    flex: 0 1 auto;
                                    min-width: 0;
                                `,
                            }}
                            data-test-selector="header_accountMenu"
                        >
                            {userName && !currentUserIsGuest && (
                                <Typography as="li">
                                    <Clickable
                                        {...styles.signOutClickable}
                                        onClick={() => {
                                            history.push(`${changePasswordPage?.url}`);
                                        }}
                                        data-test-selector="changePasswordLink"
                                    >
                                        {changePasswordPage?.title}
                                    </Clickable>
                                </Typography>
                            )}
                            {userName && !currentUserIsGuest && (
                                <Typography as="li">
                                    <Clickable
                                        {...styles.signOutClickable}
                                        onClick={onSignOutHandler}
                                        data-test-selector="headerSignOutLink"
                                    >
                                        {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                                    </Clickable>
                                </Typography>
                            )}
                        </Menu>
                    )}
                    {(!includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                        <Clickable
                            {...styles.titleClickable}
                            onClick={onSignInHandler}
                            href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
                            data-test-selector="header_signIn"
                        >
                            {!isMobile && showIcon && (
                                <Icon {...styles.titleIcon} src={icon} title={signInStatusText} />
                            )}
                            {showLabel && <Typography {...styles.titleTypography}>{signInStatusText}</Typography>}
                        </Clickable>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
